<template>
  <div :class="recipeContainerClass">
    <b-row v-if="isDemo" class="my-2">
      <b-col class="text-center">
        <div v-if="step === 'generate'">
          <p v-if="isRegistered">
            Your new project is ready!
          </p>
          <p v-else>
            Congrats, you've generated your first project using API Bakery!
            <br> &nbsp; <br>
            You can examine the generated code in the preview below.
            To save or download the project, sign up for a free account.
          </p>
          <p>
            <b-button variant="primary" href="/projects/" v-if="isRegistered">
              Save and download project
            </b-button>
            <b-button variant="primary" href="/account/signup/" v-else>
              Sign up and download project
            </b-button>
          </p>
        </div>
        <div v-else>
          <p>
            Follow the steps <span class="d-none d-lg-inline">on the left</span> to configure and build your demo Django
            project.
            <span class="d-none d-lg-inline">The panel on the right will show code preview after each step.</span>
          </p>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col lg=6 v-if="!isDemoCTA">
        <Auth v-if="step === 'auth'" :recipe="recipe" @next="onAuth($event, 'deployment')" />
        <Deployment v-if="step === 'deployment'" :recipe="recipe" @prev="onDeployment($event, 'auth')"
          @next="onDeployment($event, 'models')" />
        <ModelList v-if="step === 'models'" :recipe="recipe" @prev="onModels($event, 'deployment')"
          @next="onModels($event, 'api')" @models="onModels($event, 'models')" @apprenamed="onAppRenamed($event)"
          @modelrenamed="onModelRenamed($event)" />
        <Api v-if="step === 'api'" :recipe="recipe" @prev="onApi($event, 'models')"
          @next="onApi($event, 'code-options')" />
        <CodeOptions v-if="step === 'code-options'" :recipe="recipe" @prev="onCodeOptions($event, 'api')"
          @next="onCodeOptions($event, 'generate')" />
        <Generate v-if="!isDemo && step === 'generate'" :recipe="recipe" :generateUrl="generateUrl"
          :projectUrl="projectUrl" @prev="step = 'code-options'" @error="serverError = $event" />
      </b-col>
      <b-col :lg="previewCols" class="d-flex" style="flex-direction: column">
        <Preview :output="preparedOutput" :error="serverError" />
      </b-col>
    </b-row>
  </div>

</template>

<script>

import Preview from './preview/Preview'
import Api from './wizard/Api'
import Auth from './wizard/Auth'
import CodeOptions from './wizard/CodeOptions'
import Deployment from './wizard/Deployment'
import Generate from './wizard/Generate'
import ModelList from './wizard/ModelList'

import { saveRecipe } from '../helpers/client'

const SITE_CONFIG = window.SITE_CONFIG || {}

// Copied from app-builder/src/utils/api-client.js
function prepareOutputTree (data) {
  function createFileNode (root, path, fileData) {
    const parts = path.split('/')
    const nodeNames = parts.slice(0, parts.length - 1)
    let node = root
    for (const name of nodeNames) {
      if (!node[name]) node[name] = {}
      node = node[name]
    }
    node[parts[parts.length - 1]] = fileData
  }

  const tree = {}
  for (const path in data) {
    createFileNode(tree, path, data[path])
  }
  return tree
}

export default {
  name: 'DjangoRecipe',

  components: {
    Auth,
    Deployment,
    ModelList,
    Api,
    CodeOptions,
    Generate,
    Preview
  },

  data () {
    return {
      step: 'start',
      message: null,
      savedRecipe: null,
      serverOutput: null,
      serverError: null,
      revisionNumber: SITE_CONFIG.PROJECT_REVISION || null,
      recipe: {
        useAuth: false,
        name: SITE_CONFIG.PROJECT_NAME || '',
        description: SITE_CONFIG.PROJECT_DESCRIPTION || '',
        platform: 'django',
        django_admin: false,
        auth: null,
        apps: [
          {
            name: 'core',
            models: []
          }
        ],
        devops: {
          use_pipenv: false,
          use_docker: false,
          use_heroku: false,
          use_reverse_proxy: false
        }
      }
    }
  },

  computed: {
    isDemo () {
      return !SITE_CONFIG.GENERATE_URL
    },

    isDemoCTA () {
      return this.isDemo && this.step === 'generate'
    },

    isRegistered () {
      return !!SITE_CONFIG.IS_REGISTERED_USER
    },

    recipeContainerClass () {
      return (this.isDemoCTA) ? 'container my-4' : 'container-fluid my-4'
    },

    previewCols () {
      return (this.isDemoCTA) ? 12 : 6
    },

    generateUrl () {
      return `${SITE_CONFIG.GENERATE_URL}?rev=${this.revisionNumber}`
    },

    projectUrl () {
      return `${SITE_CONFIG.PROJECT_URL}`
    },

    preparedOutput () {
      if (!this.serverOutput) return null
      return prepareOutputTree(this.serverOutput)
    }
  },

  watch: {
    step () {
      if (!this.isDemoCTA) return

      window.localStorage.DEMO_PROJECT = JSON.stringify(this.recipe)
    }
  },

  methods: {
    onAuth ({auth, djangoAdmin}, nextStep) {
      this.$set(this.recipe, 'auth', auth)
      this.recipe.django_admin = djangoAdmin
      this.step = nextStep
      this.autoSaveRecipe()
    },

    onDeployment ({devops, useCelery}, nextStep) {
      this.recipe.devops = devops
      this.recipe.use_celery = useCelery
      this.step = nextStep
      this.autoSaveRecipe()
    },

    onModels (models, nextStep) {
      this.step = nextStep
      this.autoSaveRecipe()
    },

    onModelRenamed ({oldName, newName}) {
      for (let app of this.recipe.apps) {
        for (let model of app.models) {
          for (let field of model.fields) {
            if (field.target && field.target === oldName) {
              field.target = newName
            }
          }
        }
      }
    },

    onAppRenamed ({oldName, newName}) {
      for (let app of this.recipe.apps) {
        for (let model of app.models) {
          for (let field of model.fields) {
            if (field.target && field.target.startsWith(`${oldName}.`)) {
              field.target = newName + field.target.substr(oldName.length)
            }
          }
        }
      }
    },

    onApi ({useOpenApi, viewsets, throttling}, nextStep) {
      const modelsByName = {}

      for (let app of this.recipe.apps) {
        for (let m of app.models) {
          const fullName = `${app.name}.${m.name}`
          modelsByName[fullName] = m
          if (m.viewset && m.viewset.filter && (fullName in viewsets)) {
            viewsets[fullName].filter = m.viewset.filter
          }
          this.$set(m, 'viewset', null)
        }
      }

      for (let name in viewsets) {
        let m = modelsByName[name]
        this.$set(m, 'viewset', viewsets[name])
      }

      this.step = nextStep
      this.recipe.use_openapi = useOpenApi
      this.recipe.throttling = throttling
      this.autoSaveRecipe()
    },

    /* eslint-disable-next-line */
    async onCodeOptions ({use_black}, nextStep) {
      /* eslint-disable-next-line */
      this.recipe.use_black = !!use_black
      await this.autoSaveRecipe()
      this.step = nextStep
    },

    async autoSaveRecipe () {
      let url = SITE_CONFIG.RECIPE_SAVE_URL
      if (this.revisionNumber !== null) {
        url = url + `?rev=${this.revisionNumber}`
      }
      try {
        const { rev, output } = await saveRecipe(this.recipe, url)
        this.revisionNumber = rev
        this.serverOutput = output
        this.serverError = null
      } catch (err) {
        this.serverOutput = null
        this.serverError = JSON.stringify(err)
      }
    }
  },

  mounted () {
    const savedRecipe = SITE_CONFIG.RECIPE

    // Use saved recipe if it's not empty; however, always pick up the
    // newest recipe name and description
    if (savedRecipe && savedRecipe.platform) {
      savedRecipe.name = this.recipe.name || savedRecipe.name
      savedRecipe.description = this.recipe.description || savedRecipe.description
      this.recipe = savedRecipe
    }

    this.step = 'auth'

    this.autoSaveRecipe()
  }
}
</script>

<style lang="scss">
.box {
  border: 1px solid #ccc;
  border-radius: 0.5em;
  padding: 16px;
  background: #fff;
}

.headline {
  margin-bottom: 0.5em;
  font-size: 2.4em;
}

.headline .top {
  font-size: 0.8rem;
  color: #aaa;
}

.required {

  label,
  legend {
    font-weight: bold;
  }
}

.button-row {
  display: flex;
  justify-content: space-between;

  margin-top: 0.5em;
  padding-top: 0.5em;
  border-top: 1px solid #ccc;
}

ul.models-list {
  list-style-type: none;
  padding-left: 0;
}

.models-list li {
  margin: 0;
  padding: 0.5em 0 0.3em 0;
  border-bottom: 1px solid #ccc;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.models-list li span,
.models-list li em {
  flex: 1;
  font-size: 0.8em;
  white-space: nowrap;
  overflow: hidden;
  text-wrap: none;
  text-overflow: ellipsis;
  margin-left: 1em;
  margin-right: 1em;
}

.models-list li:nth-child(1) {
  border-top: 1px solid #ccc;
}

.autosaves {
  font-size: 0.9rem;
  margin-top: 1rem;
}
</style>



// WEBPACK FOOTER //
// src/components/DjangoRecipe.vue